/**
 * Helpers for the file-download URL the backend hands back via various
 * `download-token` / `download-file` endpoints.
 *
 * The backend tenant-scopes the file lookup by `company` AND `team`, so both are
 * attached to every download request:
 *   - `appendCompanyToUrl` appends them as query params on a `download-file` URL.
 *   - `tenantDownloadFields` returns them as body fields for the `get-download-url`
 *     POST APIs.
 *
 * Team is always sent — `none` (query) / `null` (body) when no team is selected.
 */

/** Read the currently-selected team id from localStorage, or null when none is set. */
function resolveSelectedTeamId() {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem("team_selected_id");
  if (raw == null || String(raw).trim() === "") return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Resolve the company id from an explicit value, falling back to localStorage. */
function resolveCompanyId(explicitCompanyId) {
  const raw =
    explicitCompanyId != null && explicitCompanyId !== ""
      ? explicitCompanyId
      : (typeof localStorage !== "undefined" && localStorage.getItem("company_selected")) || "";
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Append `company=<id>` and `team=<id>` to a URL's query string. Idempotent — a
 * param already present is left untouched. `team` is always appended (`none` when
 * no team is selected); `company` is appended only when one is resolvable.
 * Resilient to both absolute and relative URLs.
 */
export function appendCompanyToUrl(url, explicitCompanyId) {
  if (!url || typeof url !== "string") return url;
  let out = url;

  const companyId = resolveCompanyId(explicitCompanyId);
  if (companyId != null && !/[?&]company=/.test(out)) {
    const sep = out.includes("?") ? "&" : "?";
    out = `${out}${sep}company=${encodeURIComponent(companyId)}`;
  }

  /** Team also scopes the download-file lookup — always sent, `none` when unset. */
  if (!/[?&]team=/.test(out)) {
    const teamId = resolveSelectedTeamId();
    const teamVal = teamId != null ? String(teamId) : "none";
    const sep = out.includes("?") ? "&" : "?";
    out = `${out}${sep}team=${encodeURIComponent(teamVal)}`;
  }

  return out;
}

/**
 * Body fields for the `get-download-url` (and sibling) POST APIs. Includes
 * `company` (when resolvable) and `team` — `null` ("none") when no team is selected.
 * Spread into the request payload:
 *   getDownloadUrlFunc({ category, file_id, fileName, ...tenantDownloadFields() })
 */
export function tenantDownloadFields(explicitCompanyId) {
  const fields = {};
  const company = resolveCompanyId(explicitCompanyId);
  if (company != null) fields.company = company;
  fields.team = resolveSelectedTeamId(); // null when not found
  return fields;
}
